import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { get, post } from "../../services/api";
import Select from "react-select";
import { toast } from "react-toastify";
import CustomDatatableGrid from "../../components/CustomDatatableGrid";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import logo from "../../assets/img/logo-full.png";

function FineSetup() {
  const token = localStorage.getItem("token");
  const organizationId = localStorage.getItem("organizationId");
  const centerId = localStorage.getItem("centerId");
  const roleId = localStorage.getItem("roleId");

  const [triggerRerender, setTriggerRerender] = useState(false);


  const [centers, setCenters] = useState([]);
  const [classes, setClasses] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [feeTypes, setFeeTypes] = useState([]);
  const [fineData, setFineData] = useState([]);

  const isAdmin = roleId === "2";

  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      centerId: centerId || null,
      classIds: [],
      feeTypeIds: [],
      sessionId: null,
      daysAfterDue: "",
      fineType: "Daily",
      amount: "",
      createdBy: localStorage.getItem("userId"),
      organizationId: organizationId,
    },
  });

  const selectedCenter = watch("centerId")?.value ?? watch("centerId");

  const loadCenters = async () => {
    if (!token || !organizationId) return;
    try {
      const res = await get(
        `Dropdown/GetAllCentersByOrganizationId?organizationId=${organizationId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const options = res.map((c) => ({
        value: c.id,
        label: `${c.name} (${c.code})`,
      }));
      setCenters(options);

      if (!isAdmin && centerId) {
        const cur = options.find((o) => o.value == centerId);
        if (cur) setValue("centerId", cur.value);
      }
    } catch (err) {
      console.error("Error loading centers:", err);
    }
  };

  const loadClasses = async (centerIdVal) => {
    if (!token || !centerIdVal) {
      setClasses([]);
      return;
    }
    try {
      const res = await get(
        `CenterClass/GetClassCenterByCenterId?id=${centerIdVal}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const options = res.map((c) => ({ value: c.id, label: c.name }));
      setClasses(options);
    } catch (err) {
      console.error("Error loading classes:", err);
      setClasses([]);
    }
  };

  const loadSessions = async (centerIdVal) => {
    if (!token || !centerIdVal) {
      setSessions([]);
      return;
    }
    try {
      const res = await get(
        `FeePlan/GetCenterSessionsByCenterId?centerId=${centerIdVal}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.isSuccess) {
        const options = res.centerSessions.map((s) => ({
          value: s.id,
          label: s.sessionName,
        }));
        setSessions(options);
      }
    } catch (err) {
      console.error("Error loading sessions:", err);
      setSessions([]);
    }
  };

  const loadFeeTypes = async () => {
    if (!token || !organizationId) return;
    try {
      const res = await get(
        `FeeType/GetFeeTypeByOrganizationIdAsync?organizationId=${organizationId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.isSuccess) {
        const options = [
          { value: "ALL", label: "ALL" },
          ...res.feeType.map((f) => ({ value: f.id, label: f.name })),
        ];
        setFeeTypes(options);
      }
    } catch (err) {
      console.error("Error loading fee types:", err);
    }
  };

  const loadFineSetups = async () => {
    if (!token) return;
    try {
      const res = await get("CommonSettings/GetFineSetup", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setFineData(res || []);
    } catch (err) {
      console.error("Error loading fine setups:", err);
    }
  };

  useEffect(() => {
    loadCenters();
    loadFeeTypes();
    loadFineSetups();
  }, []);

  useEffect(() => {
    const centerIdVal = selectedCenter;

    if (centerIdVal) {
      loadClasses(centerIdVal);
      loadSessions(centerIdVal);
    } else {
      setClasses([]);
      setSessions([]);
      setValue("classIds", []);
      setValue("sessionId", null);
    }
  }, [selectedCenter]);

  const handleEdit = (row) => {
    const toSelectOptions = (idsStr, options, labelPrefix) => {
      if (!idsStr) return [];
      return idsStr
        .split(",")
        .map((id) => id.trim())
        .filter((id) => id)
        .map((id) => {
          const opt = options.find((o) => o.value == id);
          return opt || { value: id, label: `${labelPrefix} ${id}` };
        });
    };

    const feeTypeIdsStr =
      row.feeTypeIds && row.feeTypeIds.trim() !== "" ? row.feeTypeIds : null;

    reset({
      id: row.id,

      centerId: row.centerId
        ? centers.find((c) => c.value == row.centerId)?.value || row.centerId
        : null,

      classIds: row.classId
        ? [classes.find((c) => c.value == row.classId)]
        : row.classIds
        ? toSelectOptions(row.classIds, classes, "Class")
        : [],

      feeTypeIds: feeTypeIdsStr
        ? toSelectOptions(feeTypeIdsStr, feeTypes, "Fee")
        : [],

      sessionId: row.sessionId
        ? sessions.find((s) => s.value == row.sessionId)
        : null,

      daysAfterDue: row.daysAfterDue?.toString() ?? "",
      fineType: row.fineType ?? "Daily",
      amount: row.amount?.toString() ?? "",

      createdBy: row.createdBy,
      updatedBy: localStorage.getItem("userId"),
      organizationId: organizationId,
    });
  };

  /* --------------------------------------------------------------------- */
  /*  TABLE COLUMNS                                                       */
  /* --------------------------------------------------------------------- */
  /* --------------------------------------------------------------------- */
  /*  TABLE COLUMNS                                                       */
  /* --------------------------------------------------------------------- */
  const columns = React.useMemo(
    () => [
      {
        accessorKey: "mrt-row-numbers",
        header: "Sr No.",
        enableColumnOrdering: false,
        enableEditing: false, 
        enableSorting: false,
        size: 100,
        Cell: ({ row }) => row.index + 1,
      },
      {
        accessorKey: "className",
        header: "Class",
        // size: 110,
        Cell: ({ cell }) => cell.getValue() ?? "-",
      },
      {
        accessorKey: "sessionName",
        header: "Session",
        // size: 110,
        Cell: ({ cell }) => cell.getValue() ?? "-",
      },
      {
        accessorKey: "daysAfterDue",
        header: "Days After Due",
        // size: 110,
        Cell: ({ cell }) => cell.getValue() || "-",
      },
      {
        accessorKey: "fineType",
        header: "Fine Type",
        // size: 110,
        Cell: ({ cell }) => (cell.getValue() === "Daily" ? "Daily" : "One Time"),
      },
      {
        accessorKey: "amount",
        header: "Amount",
        // size: 90,
        Cell: ({ cell }) => `${cell.getValue()}`,
      },
      {
        accessorKey: "actions",
        header: "Action",
        size: 120,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <div className="d-flex gap-1">
            <button className="btn p-0" onClick={() => handleEdit(row.original)}>
              <i className="fas fa-pencil"></i>
            </button>
          </div>
        ),
      },
    ],
    []
  );

  const exportToExcel = () => {
    const filteredData = fineData.map((row, index) => ({
      "Sr No": index + 1,
      Class: row.className ?? "-",
      Session: row.sessionName ?? "-",
      "Days After Due": row.daysAfterDue || "-",
      "Fine Type": row.fineType === "Daily" ? "Daily" : "One Time",
      Amount: row.amount,
    }));

    const worksheet = XLSX.utils.json_to_sheet(filteredData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "FineSetup");
    XLSX.writeFile(workbook, "FineSetup.xlsx");
  };

  const loadImage = (url) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "Anonymous";
      img.src = url;
      img.onload = () => resolve(img);
      img.onerror = (e) => reject(e);
    });
  };

  const exportToPDF = async () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const logoWidth = 30;
    const logoHeight = 8;
    const logoX = (pageWidth - logoWidth) / 2;
    const logoY = 10;

    const roleId = localStorage.getItem("roleId");
    const orgLogoPath = localStorage.getItem("organizationLogoPath");
    const centerLogoPath = localStorage.getItem("centerLogoPath");
    const orgLogo = `${process.env.REACT_APP_LIVE_DOMAIN_URL}ExternalImages${orgLogoPath}`;
    const centerLogo = `${process.env.REACT_APP_LIVE_DOMAIN_URL}ExternalImages${centerLogoPath}`;

    let logoToUse = logo; // Default imported logo
    const sidebarLogoUrl =
      roleId == "2" ? orgLogo : roleId == "3" ? centerLogo : null;

    if (sidebarLogoUrl) {
      try {
        logoToUse = await loadImage(sidebarLogoUrl);
      } catch (error) {
        console.warn(
          "Failed to load sidebar logo, falling back to default.",
          error
        );
      }
    }

    // doc.addImage(logoToUse, "PNG", logoX, logoY, logoWidth, logoHeight);

    const title = "Fine Setup";
    const titleFontSize = 12;
    doc.setFontSize(titleFontSize);
    doc.setFont("helvetica", "bold");
    const textWidth = doc.getTextWidth(title);
    const titleX = (pageWidth - textWidth) / 2;
    const titleY = logoY + logoHeight + 10;
    doc.text(title, titleX, titleY);

    const includedColumns = columns.filter(
      (column) => column.accessorKey !== "actions" && column.accessorKey !== "mrt-row-numbers"
    );

    const headers = ["Sr No.", ...includedColumns.map((col) => col.header)];

    const bodyData = fineData.map((row, index) => {
      return [
        index + 1,
        row.className ?? "-",
        row.sessionName ?? "-",
        row.daysAfterDue || "-",
        row.fineType === "Daily" ? "Daily" : "One Time",
        row.amount,
      ];
    });

    doc.autoTable({
      head: [headers],
      body: bodyData,
      theme: "striped",
      startY: titleY + 10,
      headStyles: {
        fillColor: [132, 23, 26],
        textColor: [255, 255, 255],
      },
    });

    doc.save("FineSetup.pdf");
  };

  /* --------------------------------------------------------------------- */
  /*  SAVE                                                                */
  /* --------------------------------------------------------------------- */
  const saveData = async (data) => {
    if (!token) return toast.error("Unauthorized");

    const userId = localStorage.getItem("userId");
    const isEdit = data.id && data.id !== 0;

    const payload = {
      id: data.id ?? 0,
      centerId: data.centerId ?? null,
      classIds:
        data.classIds?.length > 0
          ? data.classIds
              .map((o) => o.value)
              .filter((v) => v !== "ALL")
              .join(",")
          : null,
      feeTypeIds:
        data.feeTypeIds?.length > 0
          ? data.feeTypeIds.map((o) => o.value).join(",")
          : null,
      sessionId: data.sessionId?.value ?? data.sessionId ?? null,
      daysAfterDue: parseInt(data.daysAfterDue, 10),
      fineType: data.fineType,
      amount: parseFloat(data.amount),
      organizationId: organizationId,

      // Always send updatedBy
      updatedBy: userId,

      // createdBy: use form value (from API on edit, userId on create)
      createdBy: isEdit ? data.createdBy : userId,
    };

    try {
      const res = await post("CommonSettings/SaveFineSetup", payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.isSuccess) {
        toast.success("Success!");
        setTriggerRerender((p) => !p);
        reset({
          centerId: centerId || null,
          classIds: [],
          feeTypeIds: [],
          sessionId: null,
          daysAfterDue: "",
          fineType: "Daily",
          amount: "",
        });
        loadFineSetups();
      } else {
        toast.error(res.message || "Save failed");
      }
    } catch (err) {
      toast.error("Error saving");
    }
  };

  /* --------------------------------------------------------------------- */
  /*  RENDER                                                              */
  /* --------------------------------------------------------------------- */
  return (
    <>
      <div className="page-header d-flex mb-2">
        <h2 className="page-heading mb-2 fs-7">Fine Setup</h2>
      </div>
      <div className="row g-3">
        {/* ==== FORM ==== */}
        <div className="col-md-5">
          <div className="card">
            <div className="card-body">
              <form
                className="row g-3 new-form-style"
                onSubmit={handleSubmit(saveData)}
              >
                {/* Center */}
                <div className="col-md-12">
                  <label className="form-label form-label-new">
                    Center <span className="text-danger fs-9">*</span>
                  </label>
                  <Controller
                    name="centerId"
                    control={control}
                    rules={{ required: "This field is required." }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={centers}
                        isDisabled={!isAdmin && centerId}
                        value={
                          centers.find((c) => c.value === field.value) || null
                        }
                        onChange={(opt) => field.onChange(opt?.value ?? null)}
                        placeholder="Select Center"
                        className="react-select-style"
                        classNamePrefix="react-select-style-pref"
                      />
                    )}
                  />
                  {errors.centerId && (
                    <small className="text-danger">
                      {errors.centerId.message}
                    </small>
                  )}
                </div>

                {/* Classes */}
                <div className="col-md-6">
                  <label className="form-label form-label-new">
                    Class <span className="text-danger fs-9">*</span>
                  </label>
                  <Controller
                    name="classIds"
                    control={control}
                    rules={{
                      validate: (value) =>
                        value && value.length > 0
                          ? true
                          : "This field is required.",
                    }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        isMulti
                        options={[{ value: "ALL", label: "ALL" }, ...classes]}
                        onChange={(opts) => {
                          if (opts && opts.some((o) => o.value === "ALL")) {
                            field.onChange([{ value: "ALL", label: "ALL" }]);
                          } else {
                            field.onChange(
                              opts ? opts.filter((o) => o.value !== "ALL") : []
                            );
                          }
                        }}
                        isDisabled={!selectedCenter}
                        className="react-select-style"
                        classNamePrefix="react-select-style-pref"
                      />
                    )}
                  />
                  {errors.classIds && (
                    <small className="text-danger">
                      {errors.classIds.message}
                    </small>
                  )}
                </div>

                {/* Session */}
                <div className="col-md-6">
                  <label className="form-label form-label-new">
                    Session <span className="text-danger fs-9">*</span>
                  </label>
                  <Controller
                    name="sessionId"
                    control={control}
                    rules={{ required: "Session is required." }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        options={sessions}
                        isDisabled={!selectedCenter}
                        placeholder="Select Session"
                        className="react-select-style"
                        classNamePrefix="react-select-style-pref"
                      />
                    )}
                  />
                  {errors.sessionId && (
                    <small className="text-danger">
                      {errors.sessionId.message}
                    </small>
                  )}
                </div>

                {/* Fee Type */}
                <div className="col-md-6">
                  <label className="form-label form-label-new">
                    Fee Type <span className="text-danger fs-9">*</span>
                  </label>
                  <Controller
                    name="feeTypeIds"
                    control={control}
                    rules={{
                      validate: (value) =>
                        value && value.length > 0
                          ? true
                          : "This field is required.",
                    }}
                    render={({ field }) => (
                      <Select
                        {...field}
                        isMulti
                        options={[
                          { value: "ALL", label: "ALL" },
                          ...feeTypes.filter((f) => f.value !== "ALL"),
                        ]}
                        onChange={(opts) => {
                          if (opts && opts.some((o) => o.value === "ALL")) {
                            // Keep "ALL" as the only selection
                            field.onChange([{ value: "ALL", label: "ALL" }]);
                          } else {
                            field.onChange(opts || []);
                          }
                        }}
                        className="react-select-style"
                        classNamePrefix="react-select-style-pref"
                      />
                    )}
                  />
                  {errors.feeTypeIds && (
                    <small className="text-danger">
                      {errors.feeTypeIds.message}
                    </small>
                  )}
                </div>

                {/* Days After Due */}
                <div className="col-md-6">
                  <label className="form-label form-label-new">
                    No. of Days after due date{" "}
                    <span className="text-danger fs-9">*</span>
                  </label>
                  <input
                    {...register("daysAfterDue", {
                      required: "This field is required.",
                      pattern: {
                        value: /^[0-9]+$/,
                        message: "Only numbers allowed",
                      },
                    })}
                    className="form-control"
                  />
                  {errors.daysAfterDue && (
                    <small className="text-danger">
                      {errors.daysAfterDue.message}
                    </small>
                  )}
                </div>

                {/* Fine Type */}
                <div className="col-md-6">
                  <label className="form-label form-label-new">
                    Fine Type <span className="text-danger fs-9">*</span>
                  </label>
                  <div className="d-flex gap-3 mt-2">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        value="Daily"
                        {...register("fineType", {
                          required: "This field is required.",
                        })}
                        id="daily"
                      />
                      <label className="form-check-label" htmlFor="daily">
                        Daily
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        value="OneTime"
                        {...register("fineType")}
                        id="onetime"
                      />
                      <label className="form-check-label" htmlFor="onetime">
                        One Time
                      </label>
                    </div>
                  </div>
                  {errors.fineType && (
                    <small className="text-danger">
                      {errors.fineType.message}
                    </small>
                  )}
                </div>

                {/* Amount */}
                <div className="col-md-6">
                  <label className="form-label form-label-new">
                    Fine Amount <span className="text-danger fs-9">*</span>
                  </label>
                  <input
                    {...register("amount", {
                      required: "This field is required.",
                      pattern: {
                        value: /^\d+(\.\d{1,2})?$/,
                        message: "Enter a valid amount (e.g. 50.00)",
                      },
                    })}
                    className="form-control"
                  />
                  {errors.amount && (
                    <small className="text-danger">
                      {errors.amount.message}
                    </small>
                  )}
                </div>

                {/* Submit */}
                <div className="col-12 mt-3">
                  <button
                    className="btn btn-primary btn-sm"
                    type="submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Saving..." : "Save"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* ==== TABLE ==== */}
        <div className="col-md-7">
          <div className="card">
            <div className="card-body">
              <CustomDatatableGrid
                columns={columns}
                data={fineData}
                onExportExcel={exportToExcel}
                onExportPDF={exportToPDF}
                initialState={{
                  columnPinning: { right: ["actions"] },
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default FineSetup;
